/**
 * Error tracking baseado em Supabase (tabela `error_logs`).
 *
 * Não usamos Sentry/PostHog externos — pra base de testers atual a tabela
 * `error_logs` no próprio Supabase é mais prática. Admin vê em uma aba do
 * painel (rota a ser adicionada quando quiser).
 *
 * Política RLS:
 *  - INSERT: anon + authenticated (qualquer um pode logar erro)
 *  - SELECT: admin only
 *
 * Características:
 *  - Rate-limit local (max 10 erros por sessão) pra não spammar a DB
 *  - Captura window.onerror + unhandledrejection automaticamente
 *  - Mantém compatibilidade com a interface antiga (`captureException`,
 *    `setSentryUser`, `initSentry`) — o resto do código não muda.
 */
import { supabase } from '../supabase';
import { APP_VERSION } from './featureFlags';

interface SentryUser {
  id?: string;
  email?: string | null;
  role?: string;
}

let currentUser: SentryUser | null = null;
let errorCount = 0;
const ERROR_LIMIT_PER_SESSION = 10;

/**
 * Registra um erro na tabela `error_logs` do Supabase.
 * Best-effort — se falhar (network, RLS), só loga no console.
 */
async function reportError(
  message: string,
  options: {
    stack?: string;
    context?: Record<string, unknown>;
  } = {},
) {
  if (errorCount >= ERROR_LIMIT_PER_SESSION) return;
  errorCount += 1;
  try {
    await supabase.from('error_logs').insert({
      user_id: currentUser?.id ?? null,
      message: message.slice(0, 1000), // limita tamanho
      stack: options.stack?.slice(0, 4000) ?? null,
      url: typeof window !== 'undefined' ? window.location.href : null,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      app_version: APP_VERSION,
      context: { ...(options.context || {}), role: currentUser?.role ?? null },
    });
  } catch (err) {
    // Silencioso — não queremos que o error tracker quebre o app.
    console.warn('[sentry] falha ao registrar erro', err);
  }
}

export function initSentry(): void {
  if (typeof window === 'undefined') return;

  // Captura erros não-tratados de JS (sync).
  window.addEventListener('error', (event) => {
    const errMsg = event.error?.message || event.message || 'unknown error';
    const stack = event.error?.stack || undefined;
    void reportError(errMsg, {
      stack,
      context: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        kind: 'window.error',
      },
    });
  });

  // Captura Promise rejections não-tratadas (async).
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === 'string'
          ? reason
          : 'unhandled promise rejection';
    const stack = reason instanceof Error ? reason.stack : undefined;
    void reportError(message, {
      stack,
      context: { kind: 'unhandledrejection' },
    });
  });

  console.info('[sentry] error tracking ativo (Supabase error_logs)');
}

export function setSentryUser(user: SentryUser | null): void {
  currentUser = user;
}

export function captureException(error: unknown, context?: Record<string, unknown>): void {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : JSON.stringify(error);
  const stack = error instanceof Error ? error.stack : undefined;
  void reportError(message, { stack, context });
}
